// ui/src/components/finance/VersionAttributionPanel.jsx
/**
 * @file VersionAttributionPanel.jsx
 * @module ui/src/components/finance/VersionAttributionPanel
 * @description
 *   Renders Finance-facing economic attribution across plan versions within a fiscal
 *   period. Split-period attribution is based exclusively on server-supplied economic
 *   event dates and effective windows; approval timing and workflow status never
 *   determine which version governs an event.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #24, #25
 *   Temporal invariants : INV-1, INV-3, INV-4
 *   Design principles : DP-1, DP-9, DP-14
 *   Cross-cutting contracts : XC-6, XC-7
 *
 * @domain-rules
 *   - Economic event date determines plan-version attribution; approval date is never
 *     used as an economic applicability signal.
 *   - Split-period attribution displays the effective windows supplied by the server
 *     and never invents, calculates, or implies proration between plan versions.
 *   - Effective boundaries are rendered from the versions payload and are never
 *     derived from plan status, period status, approval status, or other workflow state.
 *   - Financial values preserve XC-6 semantics: calculated zero, unavailable result,
 *     and incomplete/partial result remain distinct visible states.
 *   - Text communicates attribution and availability state; color may reinforce meaning
 *     but is never the sole carrier of information.
 *
 * @session-6-migration-flags
 *   - plan.effective_from / plan.effective_to — pending m_tc_01; consumed through
 *     effectiveFrom / effectiveTo in the server versions payload
 *   - credit.economic_event_date — pending m_tc_04b; event attribution data is consumed
 *     from the server versions payload
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} VersionAttributionEvent
 * @property {string} date
 *   Economic event date supplied by the server.
 * @property {number|null} amount
 *   Economic amount associated with the event.
 * @property {string|number|null} credit
 *   Credit identifier or server-provided credit description.
 * @property {string} [currency]
 *   Optional ISO currency code used when formatting the event amount.
 */

/**
 * @typedef {Object} VersionAttributionEntry
 * @property {number} planVersion
 *   Plan version integer represented as V[N].
 * @property {string} effectiveFrom
 *   Server-supplied economic governing start date.
 * @property {string|null} effectiveTo
 *   Server-supplied economic governing end date; null means open-ended.
 * @property {number} eventCount
 *   Number of economic events governed by this version.
 * @property {number|null} totalCreditValue
 *   Server-supplied aggregate credited value for this version.
 * @property {number|null} totalPayout
 *   Server-supplied calculated payout for this version.
 * @property {'available'|'not_available'|'partial'} payoutState
 *   Availability/completeness state applied to server-supplied financial results.
 * @property {VersionAttributionEvent[]} events
 *   Individual server-attributed event rows, populated when event detail is available.
 */

/**
 * @typedef {Object} VersionAttributionPanelProps
 * @property {number} planId
 *   Plan identifier used as context only; this component performs no attribution lookup.
 * @property {number} periodId
 *   Fiscal period identifier used as context only.
 * @property {VersionAttributionEntry[]} versions
 *   Server-supplied attribution entries. One entry means a single-version period;
 *   two entries represent a split-period amendment.
 */

const FINANCIAL_STATES = ['available', 'not_available', 'partial'];

const styles = {
  panel: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  header: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  context: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.8125rem',
    margin: 0,
  },
  lockedCopy: {
    margin: 0,
    whiteSpace: 'pre-line',
  },
  versionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  versionCard: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    background: 'rgb(var(--color-surface))',
    overflow: 'hidden',
  },
  versionHeader: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    justifyContent: 'space-between',
  },
  versionIdentity: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  versionBadge: {
    fontWeight: 600,
  },
  versionMeta: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.8125rem',
  },
  attributionStatement: {
    fontWeight: 500,
    margin: 0,
  },
  metrics: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  metricLabel: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  metricValue: {
    fontVariantNumeric: 'tabular-nums',
  },
  partialValue: {
    color: 'rgb(var(--color-warning))',
    fontWeight: 600,
  },
  unavailableValue: {
    color: 'rgb(var(--color-text-muted))',
    fontWeight: 500,
  },
  zeroValue: {
    fontVariantNumeric: 'tabular-nums',
  },
  eventSection: {
    borderTop: '1px solid rgb(var(--color-border))',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
  },
  eventTable: {
    width: '100%',
  },
  numericCell: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  emptyEvents: {
    margin: 0,
  },
  footerContext: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    margin: '0.75rem 0 0',
  },
};

function normalizeCurrency(currency) {
  // WHY: Event rows may omit currency metadata; null preserves the distinction
  // between an unformatted numeric value and an invented currency assumption.
  if (typeof currency !== 'string' || currency.trim() === '') {
    return null;
  }

  return currency.trim().toUpperCase();
}

function formatCurrencyValue(value, currency = null) {
  // WHY: A numeric zero is a legitimate economic result and must flow through
  // formatting rather than being treated as an absent/falsy value under XC-6.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  const normalizedCurrency = normalizeCurrency(currency);

  // WHY: Aggregate attribution payloads do not define a currency field, so the
  // component must not invent USD or another currency when none was supplied.
  if (!normalizedCurrency) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }

  try {
    return new Intl.NumberFormat(undefined, {
      currency: normalizedCurrency,
      style: 'currency',
    }).format(value);
  } catch {
    // WHY: Invalid server currency metadata must not hide a valid recorded
    // economic amount; the raw numeric representation remains auditable.
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }
}

function formatDate(value) {
  // WHY: Effective dates and event dates are economic evidence. Missing values
  // are surfaced explicitly rather than replaced with inferred boundaries.
  if (!value) {
    return 'Not available';
  }

  const date = new Date(`${value}T00:00:00`);

  // WHY: Invalid server date data must remain visibly unavailable rather than
  // allowing the UI to fabricate or silently normalize an economic boundary.
  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function FinancialValue({ value, state, label }) {
  // WHY: XC-6 requires incomplete/partial results to remain a distinct state
  // rather than being displayed as though the supplied amount were complete.
  if (state === 'partial') {
    return (
      <span
        aria-label={`${label}: Incomplete / partial`}
        style={styles.partialValue}
      >
        Incomplete / partial
      </span>
    );
  }

  // WHY: XC-6 requires absence of a result to be stated explicitly; blank
  // financial cells are not acceptable evidence on the Finance surface.
  if (state === 'not_available') {
    return (
      <span
        aria-label={`${label}: Not available`}
        style={styles.unavailableValue}
      >
        Not available
      </span>
    );
  }

  // WHY: An available state without a numeric server result is internally
  // incomplete evidence, so the UI safely exposes it as unavailable rather
  // than coercing null to zero.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return (
      <span
        aria-label={`${label}: Not available`}
        style={styles.unavailableValue}
      >
        Not available
      </span>
    );
  }

  const formattedValue = formatCurrencyValue(value);

  // WHY: This explicit branch preserves calculated zero as a real economic
  // value under XC-6 instead of suppressing it through truthiness checks.
  if (value === 0) {
    return (
      <span
        aria-label={`${label}: ${formattedValue}`}
        style={styles.zeroValue}
      >
        {formattedValue}
      </span>
    );
  }

  return (
    <span aria-label={`${label}: ${formattedValue}`}>
      {formattedValue}
    </span>
  );
}

FinancialValue.propTypes = {
  label: PropTypes.string.isRequired,
  state: PropTypes.oneOf(FINANCIAL_STATES).isRequired,
  value: PropTypes.number,
};

FinancialValue.defaultProps = {
  value: null,
};

function EventRows({ events }) {
  // WHY: Event detail is evidence supplied by the server. An empty collection
  // is represented explicitly and never backfilled or inferred client-side.
  if (!events.length) {
    return (
      <div
        className="modifier-manager-empty-state"
        role="status"
        style={styles.emptyEvents}
      >
        No event detail is available for this version.
      </div>
    );
  }

  return (
    <div className="modifier-manager-table-wrapper">
      <table
        className="modifier-manager-table"
        style={styles.eventTable}
      >
        <thead>
          <tr>
            <th scope="col">Economic event date</th>
            <th scope="col">Credit</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>

        <tbody>
          {events.map((event, index) => {
            const eventKey = [
              event.date || 'no-date',
              event.credit ?? 'no-credit',
              index,
            ].join('-');

            const formattedAmount = formatCurrencyValue(
              event.amount,
              event.currency,
            );

            return (
              <tr key={eventKey}>
                <td>{formatDate(event.date)}</td>
                <td>
                  {event.credit === null || event.credit === undefined
                    ? 'Not available'
                    : String(event.credit)}
                </td>
                <td style={styles.numericCell}>
                  {formattedAmount ?? 'Not available'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

EventRows.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      credit: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      currency: PropTypes.string,
      date: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

function VersionAttributionPanel({
  planId,
  periodId,
  versions,
}) {
  // WHY: Finance may inspect one version's underlying event evidence at a time;
  // selection is presentation state only and never changes economic attribution.
  const [selectedVersion, setSelectedVersion] = useState(null);

  // WHY: Event detail is intentionally collapsed by default so the attribution
  // summary remains scannable while preserving access to server-supplied evidence.
  const [eventDetailExpanded, setEventDetailExpanded] = useState(false);

  const isSplitPeriod = versions.length === 2;

  const currentApprovedVersion = useMemo(() => {
    // WHY: Version labels need a deterministic presentation distinction without
    // consulting workflow status. The highest version in this server-supplied
    // attribution set is the current version represented by the payload.
    if (!versions.length) {
      return null;
    }

    return Math.max(...versions.map((entry) => entry.planVersion));
  }, [versions]);

  const orderedVersions = useMemo(() => {
    // WHY: Timeline presentation follows version number only for readability.
    // Effective windows themselves remain exactly server-supplied and are never
    // calculated or reconstructed in the browser.
    return [...versions].sort(
      (left, right) => left.planVersion - right.planVersion,
    );
  }, [versions]);

  const handleToggleEvents = (planVersion) => {
    // WHY: Selecting the already-expanded version collapses its evidence without
    // changing or recomputing any server-owned economic attribution.
    if (
      selectedVersion === planVersion
      && eventDetailExpanded
    ) {
      setEventDetailExpanded(false);
      setSelectedVersion(null);
      return;
    }

    // WHY: A newly selected version becomes the sole expanded evidence context
    // so Finance does not accidentally compare event rows across version windows.
    setSelectedVersion(planVersion);
    setEventDetailExpanded(true);
  };

  // WHY: Missing attribution payload is an explicit unavailable evidence state.
  // The UI must not infer plan applicability from plan or period workflow status.
  if (!versions.length) {
    return (
      <section
        aria-labelledby={`version-attribution-title-${planId}-${periodId}`}
        className="modifier-manager-card"
        style={styles.panel}
      >
        <div
          className="modifier-manager-header"
          style={styles.header}
        >
          <h3
            id={`version-attribution-title-${planId}-${periodId}`}
            style={styles.title}
          >
            Version attribution
          </h3>
        </div>

        <div className="modifier-manager-body">
          <div
            className="modifier-manager-empty-state"
            role="status"
          >
            Version attribution is not available for this period.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby={`version-attribution-title-${planId}-${periodId}`}
      className="modifier-manager-card"
      style={styles.panel}
    >
      <div
        className="modifier-manager-header"
        style={styles.header}
      >
        <h3
          id={`version-attribution-title-${planId}-${periodId}`}
          style={styles.title}
        >
          Version attribution
        </h3>

        <p style={styles.context}>
          Plan {planId} · Fiscal period {periodId}
        </p>
      </div>

      <div className="modifier-manager-body">
        {/*
         * WHY: A split period requires the locked attribution explanation so
         * Finance understands that economic event date — not approval timing —
         * selects the governing plan version.
         */}
        {isSplitPeriod ? (
          <div
            className="modifier-manager-info"
            role="note"
          >
            <p style={styles.lockedCopy}>
              {'Economic event date determines which version governs each credit.\nApproval date does not affect event attribution.'}
            </p>
          </div>
        ) : null}

        {/*
         * WHY: The single-version branch states that one server-supplied version
         * governs the period without manufacturing a split or proration concept.
         */}
        {!isSplitPeriod && orderedVersions.length === 1 ? (
          <p style={styles.attributionStatement}>
            V{orderedVersions[0].planVersion} governs all events in this period.
          </p>
        ) : null}

        {/*
         * WHY: The split-period timeline repeats the actual effective boundaries
         * supplied by the server. No date subtraction or boundary derivation is
         * performed client-side under INV-4.
         */}
        {isSplitPeriod ? (
          <div
            aria-label="Split-period governing version timeline"
            className="modifier-manager-info"
          >
            <p style={styles.attributionStatement}>
              V{orderedVersions[0].planVersion} governs events through{' '}
              {formatDate(orderedVersions[0].effectiveTo)}.
            </p>
            <p style={styles.attributionStatement}>
              V{orderedVersions[1].planVersion} governs events from{' '}
              {formatDate(orderedVersions[1].effectiveFrom)}.
            </p>
          </div>
        ) : null}

        <div style={styles.versionList}>
          {orderedVersions.map((version) => {
            const isCurrentApproved =
              version.planVersion === currentApprovedVersion;

            const isExpanded =
              selectedVersion === version.planVersion
              && eventDetailExpanded;

            const effectiveWindowEnd =
              version.effectiveTo
                ? formatDate(version.effectiveTo)
                : 'through period end';

            return (
              <article
                aria-labelledby={`version-${version.planVersion}-title-${planId}-${periodId}`}
                className="modifier-manager-card"
                key={version.planVersion}
                style={styles.versionCard}
              >
                <div
                  className="modifier-manager-header"
                  style={styles.versionHeader}
                >
                  <div style={styles.versionIdentity}>
                    <span
                      className={`modifier-manager-badge ${
                        isCurrentApproved
                          ? 'modifier-manager-badge--complete'
                          : 'modifier-manager-badge--readonly'
                      }`}
                      style={styles.versionBadge}
                    >
                      V{version.planVersion}
                    </span>

                    <span
                      id={`version-${version.planVersion}-title-${planId}-${periodId}`}
                      style={styles.versionMeta}
                    >
                      {isCurrentApproved
                        ? 'Current approved'
                        : 'Prior version'}
                    </span>
                  </div>

                  <span style={styles.versionMeta}>
                    {formatDate(version.effectiveFrom)}
                    {' → '}
                    {effectiveWindowEnd}
                  </span>
                </div>

                <div className="modifier-manager-body">
                  <div style={styles.metrics}>
                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>
                        Economic events
                      </span>
                      <span style={styles.metricValue}>
                        {version.eventCount}{' '}
                        {version.eventCount === 1
                          ? 'economic event'
                          : 'economic events'}
                      </span>
                    </div>

                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>
                        Total credit value
                      </span>
                      <span style={styles.metricValue}>
                        <FinancialValue
                          label={`V${version.planVersion} total credit value`}
                          state={version.payoutState}
                          value={version.totalCreditValue}
                        />
                      </span>
                    </div>

                    <div style={styles.metric}>
                      <span style={styles.metricLabel}>
                        Total payout
                      </span>
                      <span style={styles.metricValue}>
                        <FinancialValue
                          label={`V${version.planVersion} total payout`}
                          state={version.payoutState}
                          value={version.totalPayout}
                        />
                      </span>
                    </div>
                  </div>

                  <div style={styles.eventSection}>
                    <button
                      aria-controls={`version-events-${version.planVersion}-${planId}-${periodId}`}
                      aria-expanded={isExpanded}
                      className="text-button"
                      onClick={() => handleToggleEvents(version.planVersion)}
                      type="button"
                    >
                      {isExpanded ? 'Hide events' : 'View events'}
                    </button>

                    {/*
                     * WHY: Event rows render only for the selected expanded
                     * version. They are the server-attributed evidence and are
                     * never redistributed or prorated by this component.
                     */}
                    {isExpanded ? (
                      <div
                        id={`version-events-${version.planVersion}-${planId}-${periodId}`}
                      >
                        <EventRows events={version.events} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p style={styles.footerContext}>
          Effective windows and event attribution shown here are supplied by the
          server. Workflow status is not used to determine economic applicability.
        </p>
      </div>
    </section>
  );
}

VersionAttributionPanel.propTypes = {
  periodId: PropTypes.number.isRequired,
  planId: PropTypes.number.isRequired,
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      effectiveFrom: PropTypes.string.isRequired,
      effectiveTo: PropTypes.string,
      eventCount: PropTypes.number.isRequired,
      events: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number,
          credit: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
          ]),
          currency: PropTypes.string,
          date: PropTypes.string.isRequired,
        }),
      ).isRequired,
      payoutState: PropTypes.oneOf(FINANCIAL_STATES).isRequired,
      planVersion: PropTypes.number.isRequired,
      totalCreditValue: PropTypes.number,
      totalPayout: PropTypes.number,
    }),
  ).isRequired,
};

/**
 * @example
 * <VersionAttributionPanel
 *   planId={418}
 *   periodId={202608}
 *   versions={[
 *     {
 *       planVersion: 3,
 *       effectiveFrom: '2026-08-01',
 *       effectiveTo: '2026-08-14',
 *       eventCount: 42,
 *       totalCreditValue: 375000,
 *       totalPayout: 18750,
 *       payoutState: 'available',
 *       events: [
 *         {
 *           date: '2026-08-12',
 *           amount: 25000,
 *           credit: 'CR-10842',
 *           currency: 'USD',
 *         },
 *       ],
 *     },
 *     {
 *       planVersion: 4,
 *       effectiveFrom: '2026-08-15',
 *       effectiveTo: null,
 *       eventCount: 31,
 *       totalCreditValue: null,
 *       totalPayout: null,
 *       payoutState: 'partial',
 *       events: [],
 *     },
 *   ]}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ #24 / #25:
 *   Finance receives explicit plan-version attribution within the fiscal period.
 *
 * ✓ INV-1:
 *   Attribution language explicitly identifies economic event date as the
 *   governing signal.
 *
 * ✓ INV-3:
 *   No plan_status, fiscal-period status, approval status, or calculation-run
 *   status participates in temporal applicability.
 *
 * ✓ INV-4:
 *   effectiveFrom, effectiveTo, event counts, and event detail are rendered
 *   directly from the server-supplied versions payload. The component performs
 *   no effective-date boundary calculation.
 *
 * ✓ DP-1:
 *   Effective governing windows remain visible on every version row.
 *
 * ✓ DP-9:
 *   Split-period presentation describes event attribution and contains no
 *   proration logic or proration language.
 *
 * ✓ DP-14 / XC-7:
 *   Current/prior, financial availability, expansion, and attribution state
 *   are communicated in text. Badge color only reinforces those states.
 *
 * ✓ XC-6:
 *   Calculated zero remains visible as zero; missing results display
 *   "Not available"; constrained results display "Incomplete / partial".
 *
 * ✓ DP-8:
 *   No Finance-specific modifier approval language is rendered.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - plan.effective_from / plan.effective_to
 *   Pending m_tc_01. This component consumes the fields through the
 *   effectiveFrom / effectiveTo properties of the server versions payload.
 *
 * - credit.economic_event_date
 *   Pending m_tc_04b. Individual event attribution is consumed from the
 *   server payload; this component does not derive event attribution locally.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - The panel and each version card have programmatically associated headings.
 * - Split-period attribution is communicated with explicit text rather than
 *   relying on timeline color.
 * - Financial availability states are exposed as text and aria-label values.
 * - The event-detail control exposes aria-expanded and aria-controls.
 * - Event details use semantic table headers with scope="col".
 * - Focus behavior is delegated to the established .text-button design-system
 *   class, including the shared :focus-visible treatment.
 * - Current/prior version meaning is written explicitly; badge color is only
 *   supplementary.
 */

export default VersionAttributionPanel;
